import { useRef, useCallback, useState, useEffect, type MutableRefObject } from "react";
import { ChevronDown } from "lucide-react";
import { InputControls } from "./InputControls";
import { AttachmentPreview, type PendingAttachment } from "./AttachmentPreview";
import { resizeImage } from "../attachments/resize";
import { MAX_FILE_SIZE, ALLOWED_IMAGE_TYPES, ALLOWED_DOCUMENT_TYPES } from "../attachments/types";
import { extractText } from "../attachments/extract";
import type { ModelEntry, ChatConfig, EffortLevel } from "../shared/chat-config";
import { getStoredToken } from "../hooks/useAuth";
import { useI18n } from "../i18n";
import { useSpeechToText } from "../hooks/useSpeechToText";
import { useVoiceRecorder } from "../hooks/useVoiceRecorder";
import { useStreamingSTT, type SttStreamingProps } from "../hooks/useStreamingSTT";
// Must track ALL_ALLOWED_TYPES in src/attachments/types.ts
const ALLOWED_EXTENSIONS = "image/*,text/plain,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.md,.txt,.pdf,.docx";

interface ChatInputProps {
  onSend: (message: string, attachments?: PendingAttachment[]) => void;
  onStop: () => void;
  isLoading: boolean;
  /** ADR-023: text handed back by a take-back, inserted once per distinct key. */
  takeBackDraft?: { key: string; text: string } | null;
  disabled: boolean;
  switching?: boolean;
  config: ChatConfig;
  currentModel: ModelEntry;
  availableModels: ModelEntry[];
  onModelChange: (modelId: string) => void;
  onEffortChange: (effort: EffortLevel) => void;
  onToggleThinking: () => void;
  activeSessionId?: string | null;
  voiceMode?: boolean;
  onToggleVoiceMode?: () => void;
  onStopSpeaking?: () => void;
  /** Controlled collapse state — owned by ChatArea via useBarCollapse */
  barExpanded: boolean;
  onToggleBar: () => void;
  onCollapseBar: () => void;
  onExpandBar: () => void;
  prefersReducedMotion: boolean;
  /** Server-side STT (Whisper). When available, replaces Web Speech API. */
  sttAvailable?: boolean;
  sendSTTAudio?: (blob: Blob, format?: string) => void;
  onSTTResultRef?: MutableRefObject<((text: string) => void) | null>;
  /** Streaming STT plumbing — live-as-you-speak transcription. */
  sttStreaming?: SttStreamingProps;
}

/**
 * A running reply does NOT block a send: a message typed mid-reply is an
 * interjection (ADR-023), queued server-side and read at the companion's next pause.
 */
export function isChatSubmitBlocked(
  hasUploading: boolean,
  disabled: boolean,
  sttFinalizing: boolean,
): boolean {
  return hasUploading || disabled || sttFinalizing;
}

export function ChatInput({
  onSend,
  onStop,
  isLoading,
  takeBackDraft,
  disabled,
  switching,
  config,
  currentModel,
  availableModels,
  onModelChange,
  onEffortChange,
  onToggleThinking,
  activeSessionId,
  voiceMode,
  onToggleVoiceMode,
  onStopSpeaking,
  barExpanded,
  onToggleBar,
  onCollapseBar,
  onExpandBar,
  prefersReducedMotion,
  sttAvailable,
  sendSTTAudio,
  onSTTResultRef,
  sttStreaming,
}: ChatInputProps) {
  const { t } = useI18n();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  // Recap indicator: 'idle' | 'loading' | 'saved' | 'error'
  const [recapState, setRecapState] = useState<'idle' | 'loading' | 'saved' | 'error'>('idle');
  const recapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Touch tracking for swipe gesture on the bar
  const touchStartY = useRef<number | null>(null);

  // STT — prefix text already in textarea when dictation starts
  const prefixRef = useRef('');
  // Transcribing state: true from blob send until stt_result arrives
  const [isTranscribing, setIsTranscribing] = useState(false);
  const transcribingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track manual vs auto stop for streaming — manual plays its own tone in handleMicToggle
  const manualStopRef = useRef(false);
  const prevStreamingRef = useRef(false);

  // Preserve draft across reconnects — save to sessionStorage on every input,
  // restore on mount if the textarea is empty
  const draftKey = `muse-draft-${activeSessionId ?? 'default'}`;
  useEffect(() => {
    const saved = sessionStorage.getItem(draftKey);
    if (saved && textareaRef.current && !textareaRef.current.value) {
      textareaRef.current.value = saved;
      autoResize();
    }
  }, [draftKey]);

  // ADR-023: a taken-back message returns to the composer, ahead of anything already typed.
  const lastTakeBackKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!takeBackDraft || lastTakeBackKeyRef.current === takeBackDraft.key) return;
    lastTakeBackKeyRef.current = takeBackDraft.key;
    const el = textareaRef.current;
    if (!el) return;
    el.value = el.value ? `${takeBackDraft.text}\n\n${el.value}` : takeBackDraft.text;
    autoResize();
    el.focus();
  }, [takeBackDraft]);

  function autoResize() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxH = Math.floor(window.innerHeight * 0.5);
    el.style.height = Math.min(el.scrollHeight, maxH) + 'px';
  }

  const handleTranscript = useCallback((text: string) => {
    if (textareaRef.current) {
      textareaRef.current.value = prefixRef.current ? `${prefixRef.current} ${text}` : text;
      autoResize();
    }
  }, []);

  // ── Tier 3: Web Speech API — browser-native fallback ──
  const { isListening: webSttListening, toggle: webSttToggle, supported: sttSupported } = useSpeechToText(handleTranscript);

  // ── Tier 2: Batch server STT — record blob, send via WS ──
  const handleRecordingResult = useCallback((blob: Blob) => {
    console.log('[stt-batch] blob ready:', blob.size, 'bytes');
    sendSTTAudio?.(blob, 'webm');
    setIsTranscribing(true);
    // Safety timeout — Whisper on CPU can take 20-30s for long recordings
    if (transcribingTimerRef.current) clearTimeout(transcribingTimerRef.current);
    transcribingTimerRef.current = setTimeout(() => {
      console.warn('[stt-batch] transcription timed out (45s)');
      setIsTranscribing(false);
    }, 45_000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendSTTAudio]);

  const recorder = useVoiceRecorder(handleRecordingResult);

  // Wire batch STT result
  useEffect(() => {
    if (onSTTResultRef) {
      onSTTResultRef.current = (text: string) => {
        if (transcribingTimerRef.current) {
          clearTimeout(transcribingTimerRef.current);
          transcribingTimerRef.current = null;
        }
        setIsTranscribing(false);
        if (text) {
          handleTranscript(text);
        }
      };
    }
    return () => {
      if (onSTTResultRef) onSTTResultRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleTranscript, onSTTResultRef]);

  // ── Tier 1: Streaming STT — live text as you speak ──
  const streaming = useStreamingSTT({
    sendStreamStart: sttStreaming?.sendStart ?? (() => {}),
    sendStreamStop: sttStreaming?.sendStop ?? (() => {}),
    sendBinaryFrame: sttStreaming?.sendBinary ?? (() => {}),
    onSegmentRef: sttStreaming?.onSegmentRef ?? { current: null },
    onReadyRef: sttStreaming?.onReadyRef ?? { current: null },
    onDoneRef: sttStreaming?.onDoneRef ?? { current: null },
    onDisconnectRef: sttStreaming?.onDisconnectRef ?? { current: null },
    onErrorRef: sttStreaming?.onErrorRef ?? { current: null },
  });

  // Update textarea with streaming transcript
  useEffect(() => {
    if (streaming.isStreaming || streaming.fullTranscript) {
      if (textareaRef.current) {
        const prefix = prefixRef.current;
        textareaRef.current.value = prefix
          ? `${prefix} ${streaming.fullTranscript}`
          : streaming.fullTranscript;
        autoResize();
      }
    }
  }, [streaming.fullTranscript, streaming.isStreaming]);

  // Auto-stop notification — audible feedback when streaming ends without user action
  // (VAD silence timeout, duration cap, server error). Manual stops play their own
  // tone in handleMicToggle, so we skip those via manualStopRef.
  useEffect(() => {
    if (prevStreamingRef.current && !streaming.isStreaming && !manualStopRef.current) {
      // Descending two-tone: "session ended, tap mic to continue"
      playTone(440, 100, 0.12);
      setTimeout(() => playTone(330, 150, 0.12), 120);
    }
    manualStopRef.current = false;
    prevStreamingRef.current = streaming.isStreaming;
  }, [streaming.isStreaming]);

  // ── Three-tier STT selection ──
  // Priority: streaming > batch > web speech
  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const canStreamingSTT = Boolean(sttStreaming?.available) && streaming.supported;
  const canBatchSTT = Boolean(sttAvailable) && recorder.supported;
  const canWebSpeech = sttSupported;

  const activeSTTMode: 'streaming' | 'batch' | 'web-speech' | null =
    canStreamingSTT ? 'streaming'
    : (canBatchSTT && (isAndroid || !canWebSpeech)) ? 'batch'
    // Android Chrome's SpeechRecognition is unreliable — onend doesn't fire
    // after stop(), continuous mode duplicates words 5-8×, and the beep-per-
    // utterance UX is hostile. Disable web-speech on Android entirely.
    : (canWebSpeech && !isAndroid) ? 'web-speech'
    : null;

  // Log mode selection on first render and when it changes
  const prevModeRef = useRef<string | null>(null);

  if (prevModeRef.current !== activeSTTMode) {
    console.log('[stt-tier]', activeSTTMode ?? 'none', {
      streamAvail: sttStreaming?.available, streamSupported: streaming.supported,
      batchAvail: sttAvailable, recorderSupported: recorder.supported,
      webSpeech: sttSupported, isAndroid,
    });
    prevModeRef.current = activeSTTMode;
    // Server-visible diagnostic — fires on every tier change (including initial).
    // No DevTools needed: data appears in gateway journalctl.
    try {
      navigator.sendBeacon?.('/api/stt-diag', JSON.stringify({
        mode: activeSTTMode,
        streamAvail: sttStreaming?.available, streamSupp: streaming.supported,
        batchAvail: sttAvailable, recSupp: recorder.supported,
        webSpeech: sttSupported, android: isAndroid,
        ua: navigator.userAgent.slice(0, 200),
      }));
    } catch { /* beacon unsupported — ignore */ }
  }

  const canSTT = activeSTTMode !== null;

  // Unified listening state for the mic button
  const isListening = activeSTTMode === 'streaming'
    ? streaming.isStreaming
    : activeSTTMode === 'batch'
      ? (recorder.isRecording || isTranscribing)
      : activeSTTMode === 'web-speech'
        ? webSttListening
        : false; // null mode — no STT, button disabled

  /** Short tone via Web Audio API — no external audio files needed. */
  function playTone(freq: number, durationMs = 80, volume = 0.15) {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + durationMs / 1000);
      // Auto-cleanup
      osc.onended = () => ctx.close();
    } catch { /* silent fail on browsers that block AudioContext */ }
  }

  function handleMicToggle() {
    // Diagnostic breadcrumb — survives production minification (console.warn, not .log).
    // Visible in browser DevTools and helps diagnose "mic not working" remotely.
    console.warn('[mic-toggle]', activeSTTMode ?? 'NO_MODE', {
      streaming: streaming.isStreaming, batch: recorder.isRecording,
      sttAvail: sttAvailable, streamAvail: sttStreaming?.available,
    });
    if (!activeSTTMode) return;
    if (activeSTTMode === 'streaming') {
      if (!streaming.isStreaming) {
        prefixRef.current = textareaRef.current?.value.trim() || '';
        // A fresh mic session appends to the current composer via prefixRef.
        // Clear the hook's prior session first or the previous transcript is
        // included both in the prefix and again in fullTranscript.
        streaming.reset();
        onExpandBar();
        playTone(880, 80);
      } else {
        manualStopRef.current = true;
        playTone(440, 120);
      }
      streaming.toggle();
    } else if (activeSTTMode === 'batch') {
      // If currently transcribing, let the user cancel and start fresh
      if (isTranscribing) {
        if (transcribingTimerRef.current) {
          clearTimeout(transcribingTimerRef.current);
          transcribingTimerRef.current = null;
        }
        setIsTranscribing(false);
        return;
      }
      if (!recorder.isRecording) {
        prefixRef.current = textareaRef.current?.value.trim() || '';
        onExpandBar();
        playTone(880, 80);
      } else {
        playTone(440, 120);
      }
      recorder.toggle();
    } else if (activeSTTMode === 'web-speech') {
      if (!webSttListening) {
        prefixRef.current = textareaRef.current?.value.trim() || '';
        onExpandBar();
      }
      webSttToggle();
    }
  }

  function readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  async function processFiles(files: FileList | File[]) {
    const fileArray = Array.from(files);
    for (const rawFile of fileArray) {
      if (rawFile.size > MAX_FILE_SIZE) {
        console.warn(`[attach] ${rawFile.name} too large`);
        continue;
      }

      const file = ALLOWED_IMAGE_TYPES.has(rawFile.type) ? await resizeImage(rawFile) : rawFile;

      const localId = crypto.randomUUID();
      const previewUrl = ALLOWED_IMAGE_TYPES.has(file.type) ? URL.createObjectURL(file) : undefined;

      const pending: PendingAttachment = {
        id: localId,
        file,
        status: "uploading",
        previewUrl,
      };

      setAttachments(prev => [...prev, pending]);

      try {
        if (ALLOWED_IMAGE_TYPES.has(file.type)) {
          const dataUrl = await readFileAsDataURL(file);
          setAttachments(prev =>
            prev.map(a => a.id === localId ? {
              ...a,
              status: "ready" as const,
              serverResponse: {
                id: localId,
                filename: file.name,
                media_type: file.type,
                size_bytes: file.size,
                url: dataUrl,
              },
            } : a)
          );
        } else if (ALLOWED_DOCUMENT_TYPES.has(file.type)) {
          const text = await extractText(file, file.type);
          setAttachments(prev =>
            prev.map(a => a.id === localId ? {
              ...a,
              status: text !== null ? "ready" as const : "error" as const,
              serverResponse: text !== null ? {
                id: localId,
                filename: file.name,
                media_type: file.type,
                size_bytes: file.size,
                extractedText: text,
              } : undefined,
              error: text !== null ? undefined : "Could not extract text from file",
            } : a)
          );
        } else {
          setAttachments(prev =>
            prev.map(a => a.id === localId ? {
              ...a,
              status: "error" as const,
              error: "Unsupported file type",
            } : a)
          );
        }
      } catch {
        setAttachments(prev =>
          prev.map(a => a.id === localId ? {
            ...a,
            status: "error" as const,
            error: "Failed to read file",
          } : a)
        );
      }
    }
  }

  function handleAttachClick() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = "";
    }
  }

  function handleRemoveAttachment(id: string) {
    setAttachments(prev => {
      const att = prev.find(a => a.id === id);
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      return prev.filter(a => a.id !== id);
    });
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }

  const hasUploading = attachments.some(a => a.status === "uploading");

  const handleSubmit = useCallback(() => {
    const value = textareaRef.current?.value.trim();
    const readyAttachments = attachments.filter(a => a.status === "ready");

    if (isChatSubmitBlocked(hasUploading, disabled, streaming.isFinalizing)) return;
    if (!value && readyAttachments.length === 0) return;

    onSend(value || "", readyAttachments.length > 0 ? readyAttachments : undefined);

    if (textareaRef.current) {
      textareaRef.current.value = "";
      textareaRef.current.style.height = "auto";
    }
    // Reset streaming STT accumulated text after sending
    streaming.reset();
    prefixRef.current = '';
    sessionStorage.removeItem(draftKey);
    attachments.forEach(a => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
    setAttachments([]);
  }, [onSend, disabled, attachments, hasUploading, draftKey, streaming]);

  const handleSummarize = useCallback(async () => {
    if (isLoading || disabled || recapState === 'loading' || !activeSessionId) return;

    // Clear any pending fade-out timer
    if (recapTimerRef.current) {
      clearTimeout(recapTimerRef.current);
      recapTimerRef.current = null;
    }
    setRecapState('loading');

    try {
      const token = getStoredToken();
      const res = await fetch(`/api/sessions/${encodeURIComponent(activeSessionId)}/recap`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token ?? ''}`,
          'Content-Type': 'application/json',
        },
      });

      if (res.ok) {
        setRecapState('saved');
        recapTimerRef.current = setTimeout(() => setRecapState('idle'), 2500);
      } else {
        setRecapState('error');
        recapTimerRef.current = setTimeout(() => setRecapState('idle'), 2500);
      }
    } catch {
      setRecapState('error');
      recapTimerRef.current = setTimeout(() => setRecapState('idle'), 2500);
    }
  }, [isLoading, disabled, recapState, activeSessionId]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && e.metaKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  // Auto-expand textarea
  function handleInput(e: React.FormEvent<HTMLTextAreaElement>) {
    const el = e.currentTarget;
    el.style.height = "auto";
    const maxH = Math.floor(window.innerHeight * 0.5);
    el.style.height = Math.min(el.scrollHeight, maxH) + "px";
    sessionStorage.setItem(draftKey, el.value);
  }

  // Focus on any bar control expands the bar (a11y: focus should never land on off-screen element)
  function handleBarFocus() {
    if (!barExpanded) onExpandBar();
  }

  // Swipe detection on the bar card
  function handleTouchStart(e: React.TouchEvent) {
    touchStartY.current = e.touches[0].clientY;
  }

  function handleTouchEnd(e: React.TouchEvent) {
    if (touchStartY.current === null) return;
    const delta = e.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    const SWIPE_THRESHOLD = 30;
    if (delta > SWIPE_THRESHOLD && barExpanded) {
      // swipe down → collapse (explicit, direction-safe)
      onCollapseBar();
    } else if (delta < -SWIPE_THRESHOLD && !barExpanded) {
      // swipe up on handle strip → expand
      onExpandBar();
    }
  }

  // When bar collapses, blur textarea so keyboard closes on mobile
  useEffect(() => {
    if (!barExpanded && document.activeElement === textareaRef.current) {
      textareaRef.current?.blur();
    }
  }, [barExpanded]);

  // Cleanup recap indicator timer on unmount
  useEffect(() => {
    return () => {
      if (recapTimerRef.current) clearTimeout(recapTimerRef.current);
    };
  }, []);

  const chevronLabel = barExpanded ? t("controls.collapseBar") : t("controls.expandBar");

  return (
    <div
      className={`chat-input-wrapper${barExpanded ? "" : " bar-collapsed"}${prefersReducedMotion ? " no-motion" : ""}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Chevron handle — always visible ── */}
      <button
        className="chat-bar-handle"
        onClick={onToggleBar}
        aria-label={chevronLabel}
        aria-expanded={barExpanded}
        aria-controls="chat-input-card"
      >
        <ChevronDown
          size={16}
          className={`chat-bar-handle__icon${barExpanded ? "" : " rotated"}`}
          aria-hidden="true"
        />
      </button>

      {/* ── Collapsible card ── */}
      <div
        id="chat-input-card"
        className={`chat-input-card${dragOver ? " drag-over" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onFocus={handleBarFocus}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS}
          multiple
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <textarea
          ref={textareaRef}
          className="chat-input-field"
          placeholder="Message MUSE..."
          rows={1}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          tabIndex={barExpanded ? 0 : -1}
        />
        {/* STT status — visible feedback for recording / transcribing */}
        {(recorder.isRecording || isTranscribing || streaming.isStreaming || streaming.isFinalizing) && (
          <div className={`stt-status${isTranscribing ? ' stt-status--transcribing' : recorder.isRecording ? ' stt-status--recording' : ' stt-status--streaming'}`}>
            <span className="stt-status__dot" />
            <span className="stt-status__label">
              {isTranscribing
                ? 'Transcribing…'
                : recorder.isRecording
                  ? 'Recording — tap mic to stop'
                  : streaming.isFinalizing
                    ? 'Finalizing speech…'
                    : 'Listening…'}
            </span>
          </div>
        )}
        <AttachmentPreview
          attachments={attachments}
          onRemove={handleRemoveAttachment}
        />
        {recapState !== 'idle' && (
          <div
            className={`recap-indicator recap-indicator--${recapState}`}
            aria-live="polite"
            aria-atomic="true"
          >
            {recapState === 'loading' && 'Saving recap…'}
            {recapState === 'saved' && '✓ Recap saved'}
            {recapState === 'error' && 'Recap failed'}
          </div>
        )}
        <InputControls
          config={config}
          currentModel={currentModel}
          availableModels={availableModels}
          onModelChange={onModelChange}
          onEffortChange={onEffortChange}
          isLoading={isLoading}
          switching={switching}
          onStop={() => { onStop(); onStopSpeaking?.(); }}
          onSend={handleSubmit}
          onSummarize={handleSummarize}
          onAttach={handleAttachClick}
          disabled={disabled || streaming.isFinalizing}
          uploading={hasUploading}
          voiceMode={voiceMode}
          onToggleVoiceMode={onToggleVoiceMode}
          onMicToggle={canSTT ? handleMicToggle : undefined}
          isListening={isListening}
          isTranscribing={isTranscribing}
        />
        {canSTT && (
          <span className="sr-only" aria-live="polite" aria-atomic="true">
            {(streaming.error || recorder.error)
              ? `Microphone error: ${streaming.error || recorder.error}`
              : streaming.isStreaming
                ? 'Streaming...'
                : streaming.isFinalizing
                  ? 'Finalizing speech...'
                : recorder.isRecording
                  ? 'Recording...'
                  : isTranscribing
                    ? 'Transcribing...'
                    : webSttListening
                      ? 'Listening...'
                      : ''}
          </span>
        )}
      </div>
    </div>
  );
}
